import { fetchGetAccounts, fetchGetApplication } from '@iwoca/lapi-client/edge';
import { QueryClient } from '@tanstack/react-query';

import { wait } from '../../../utils/Wait';
import { processOpenApplication } from '../../Account/hooks/processOpenApplication';

export const MAX_POLL_ATTEMPTS = 30;
export const POLL_INTERVAL_MS = 1000;

const QUERIES_TO_REMOVE = [
  'fetchGetAccounts',
  'fetchGetIwocapayPaymentLater',
  'fetchGetIwocapayPaymentCaptured',
  'fetchGetApplication',
  'fetchGetFormalOffers',
] as const;

const QUERIES_TO_RESET = ['fetchGetBuyerSpendingLimit'] as const;

export type DecisionOutcome =
  | 'approved'
  | 'partial'
  | 'declined'
  | 'blocked'
  | 'timeout';

export function clearPostDecisionQueries(queryClient: QueryClient) {
  queryClient.removeQueries({
    predicate: (query) =>
      (QUERIES_TO_REMOVE as readonly string[]).includes(
        query.queryKey[0] as string,
      ),
  });

  queryClient.resetQueries({
    predicate: (query) =>
      (QUERIES_TO_RESET as readonly string[]).includes(
        query.queryKey[0] as string,
      ),
  });
}

export async function runDecisionPolling({
  stateKey,
  payLinkAmount,
  isCancelled,
  pollIntervalMs = POLL_INTERVAL_MS,
  maxPollAttempts = MAX_POLL_ATTEMPTS,
}: {
  stateKey: string;
  payLinkAmount?: number;
  isCancelled: () => boolean;
  pollIntervalMs?: number;
  maxPollAttempts?: number;
}): Promise<DecisionOutcome | null> {
  try {
    if (isCancelled()) return null;

    const applicationResponse = await fetchGetApplication({ stateKey });

    const isOpenIwocaPay =
      applicationResponse.data?.status === 'open' &&
      !!applicationResponse.data?.requested_products?.some(
        (p) => p.product_type === 'iwocapay',
      );

    if (isOpenIwocaPay) {
      if (isCancelled()) return null;
      const result = await processOpenApplication(stateKey);
      if (result === 'declined') return 'declined';
      if (result === 'blocked') return 'blocked';
    }
  } catch {
    // Formalisation failed — fall through to polling, which will either
    // find an existing valid offer or time out.
  }

  for (let attempt = 0; attempt < maxPollAttempts; attempt++) {
    if (isCancelled()) return null;

    try {
      const response = await fetchGetAccounts({ stateKey });

      let hasValidOffer = false;
      for (const account of response.data?.accounts ?? []) {
        for (const offer of account.formal_offers ?? []) {
          if (offer.product_type !== 'iwocapay' || !offer.is_valid) continue;
          if (
            payLinkAmount === undefined ||
            (offer.available_cash ?? 0) >= payLinkAmount
          ) {
            return 'approved';
          }
          hasValidOffer = true;
        }
      }

      if (hasValidOffer) return 'partial';
    } catch {
      // Retry on next attempt
    }

    await wait(pollIntervalMs);
  }

  return 'timeout';
}
